import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClients, createClient, createDevis } from '../services/api';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const NewDevisPage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showClientModal, setShowClientModal] = useState(false);
  const [newClient, setNewClient] = useState({ nom: '', adresse: '', contact: '', telephone: '', email: '' });
  const [devisData, setDevisData] = useState({
    client_id: '',
    objet: '',
    date_remise: '',
    version: '1',
    contact_maag: '',
    surface_commerciale: 0,
    surface_annexes: 0
  });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const res = await getClients();
      setClients(res.data);
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      const res = await createClient(newClient);
      toast.success('Client créé');
      setShowClientModal(false);
      setNewClient({ nom: '', adresse: '', contact: '', telephone: '', email: '' });
      await loadClients();
      setDevisData({ ...devisData, client_id: res.data.id });
    } catch (e) {
      toast.error('Erreur lors de la création du client');
    }
  };

  const handleCreateDevis = async (e) => {
    e.preventDefault();
    try {
      const res = await createDevis(devisData);
      toast.success('Devis créé');
      navigate(`/devis/${res.data.id}`);
    } catch (e) {
      toast.error('Erreur lors de la création');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="new-devis-page">
      <div className="mb-6">
        <Link 
          to="/devis"
          className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Retour aux devis
        </Link>
      </div>

      <div className="max-w-2xl">
        <h1 className="text-3xl font-bold text-slate-900 mb-6">Nouveau devis</h1>

        <form onSubmit={handleCreateDevis} className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm space-y-6">
          {/* Client selection */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Client *
            </label>
            <div className="flex gap-2">
              <select
                value={devisData.client_id}
                onChange={(e) => setDevisData({ ...devisData, client_id: e.target.value })}
                className="flex-1 h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                required
                data-testid="select-client"
              >
                <option value="">Sélectionner un client</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.nom}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setShowClientModal(true)}
                className="h-10 px-4 border border-slate-300 text-slate-700 rounded-md font-medium hover:bg-slate-50 flex items-center"
                data-testid="new-client-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Nouveau
              </button>
            </div>
          </div>

          {/* Objet */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Objet du devis *
            </label>
            <input
              type="text"
              value={devisData.objet}
              onChange={(e) => setDevisData({ ...devisData, objet: e.target.value })}
              placeholder="Ex: ESTIMATIF RENOVATION MAGASIN LYON"
              className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              required
              data-testid="devis-objet"
            />
          </div>

          {/* Date et version */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Date de remise
              </label>
              <input
                type="date"
                value={devisData.date_remise}
                onChange={(e) => setDevisData({ ...devisData, date_remise: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                data-testid="devis-date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Version
              </label>
              <input
                type="text"
                value={devisData.version}
                onChange={(e) => setDevisData({ ...devisData, version: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                data-testid="devis-version"
              />
            </div>
          </div>

          {/* Contact MAAG */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Contact MAAG
            </label>
            <input
              type="text"
              value={devisData.contact_maag}
              onChange={(e) => setDevisData({ ...devisData, contact_maag: e.target.value })}
              className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              data-testid="devis-contact"
            />
          </div>

          {/* Surfaces */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Surface commerciale (m²)
              </label>
              <input
                type="number"
                value={devisData.surface_commerciale}
                onChange={(e) => setDevisData({ ...devisData, surface_commerciale: parseFloat(e.target.value) || 0 })}
                className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                data-testid="devis-surface"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Surface annexes (m²)
              </label>
              <input
                type="number"
                value={devisData.surface_annexes}
                onChange={(e) => setDevisData({ ...devisData, surface_annexes: parseFloat(e.target.value) || 0 })}
                className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                data-testid="devis-annexes"
              />
            </div>
          </div>

          <div className="pt-4">
            <button
              type="submit"
              className="w-full h-10 bg-slate-900 text-white rounded-md font-medium hover:bg-slate-800 transition-colors"
              data-testid="create-devis-submit"
            >
              Créer le devis
            </button>
          </div>
        </form>
      </div>

      {/* Client Modal */}
      {showClientModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4" data-testid="client-modal">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">Nouveau client</h2>
              <button onClick={() => setShowClientModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleCreateClient} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nom *</label>
                <input
                  type="text"
                  value={newClient.nom}
                  onChange={(e) => setNewClient({...newClient, nom: e.target.value})}
                  className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                  required
                  data-testid="modal-client-nom"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Adresse</label>
                <input
                  type="text"
                  value={newClient.adresse}
                  onChange={(e) => setNewClient({...newClient, adresse: e.target.value})}
                  className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Contact</label>
                <input
                  type="text"
                  value={newClient.contact}
                  onChange={(e) => setNewClient({...newClient, contact: e.target.value})}
                  className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone</label>
                  <input
                    type="text"
                    value={newClient.telephone}
                    onChange={(e) => setNewClient({...newClient, telephone: e.target.value})}
                    className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={newClient.email}
                    onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                    className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowClientModal(false)}
                  className="flex-1 h-10 border border-slate-300 text-slate-700 rounded-md font-medium hover:bg-slate-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 h-10 bg-slate-900 text-white rounded-md font-medium hover:bg-slate-800"
                  data-testid="modal-create-client"
                >
                  Créer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewDevisPage;
